/**
 * ProseSection — a self-contained "part" of a long-form page: an optional
 * eyebrow + heading and a figure that floats so the body text wraps around it
 * (magazine layout), all inside a Card so the page reads as discrete containers
 * rather than one long list of text. Composes the Card + Figure primitives +
 * tokens — no bespoke markup. Children are the body blocks (prose, a centred
 * lead-excerpt pull-quote, tables, …) rendered by the caller.
 *
 * Body prose spans the full card width. A section's scannable "excerpt" is a
 * centred lead pull-quote rendered inline among the children (emitted by the
 * md→blocks compiler from `@ix:aside`), not a side float.
 */
import { Children, createElement, type ReactNode } from 'react';
import { Card } from '../card';
import { Figure } from './Figure';
import { cn } from '../../utils';

/** `*word*` → editorial italic emphasis (Instrument Serif, accent), matching
 *  SectionHeader/Hero — so emphasis in a card title (e.g. a role's date range)
 *  renders italic instead of showing literal `*`. */
const editorialize = (text: string): string =>
  text.replace(/\*([^*]+)\*/g, '<em class="font-editorial font-normal text-primary">$1</em>');

export interface ProseSectionProps {
  readonly title?: string;
  readonly eyebrow?: string;
  /** Which side the floated figure sits on (alternated by the caller). */
  readonly side?: 'left' | 'right';
  /** Larger heading for a section's lead card vs. a subsection card. */
  readonly lead?: boolean;
  readonly figure?: { readonly src: string; readonly alt?: string; readonly caption?: string };
  readonly children?: ReactNode;
}

export function ProseSection({ title, eyebrow, side = 'right', lead = false, figure, children }: ProseSectionProps) {
  const hasBody = Children.count(children) > 0;
  const caption = figure?.caption ? { caption: figure.caption } : {};
  // A section-lead diagram is wide/schematic → it sits full-width under the
  // intro. Photos (and all subsection images) float so text wraps around them.
  const isDiagram = figure ? /\/diagrams\//.test(figure.src) : false;
  return (
    <Card className="overflow-hidden p-(--space-card-lg)">
      {eyebrow && <p className="mb-(--space-1) font-mono text-xs uppercase tracking-wider text-muted-foreground">{eyebrow}</p>}
      {title && createElement(lead ? 'h2' : 'h3', {
        // Lead cards are a page's top-level sections (h2 under the hero h1);
        // subsection cards nest beneath them (h3). Keeps the heading hierarchy
        // contiguous for screen readers / Lighthouse without changing the look
        // (size is set by the class, not the tag).
        className: cn('mb-(--space-3) font-display font-semibold text-foreground', lead ? 'text-2xl' : 'text-xl'),
        dangerouslySetInnerHTML: { __html: editorialize(title) },
      })}

      {!figure ? (
        // Text-only card: body spans the full card width, blocks on one rhythm.
        <div className="space-y-(--space-block)">{children}</div>
      ) : lead && isDiagram ? (
        // Section lead: full-width intro, then the wide diagram beneath it.
        <div className="space-y-(--space-block)">
          {hasBody && children}
          <Figure src={figure.src} alt={figure.alt ?? title ?? ''} {...caption} />
        </div>
      ) : (
        // Subsection: the image floats and the body text wraps around it.
        <>
          <Figure src={figure.src} alt={figure.alt ?? title ?? ''} bare {...caption} {...(hasBody ? { float: side } : {})} />
          <div className="space-y-(--space-block)">{children}</div>
        </>
      )}
      <div className="clear-both" />
    </Card>
  );
}
